import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const INVENTORY_FIELDS = [
  { key: 'inv_prompts', name: 'Prompt Library', hint: 'How many prompts? Where is the library? Top 3 use cases:', height: 70 },
  { key: 'inv_workflow', name: 'Workflow Redesign', hint: 'Which workflow? Time before vs after? Annualized savings:', height: 70 },
  { key: 'inv_agent_spec', name: 'Agent Spec + Log', hint: 'Agent name and version? Current status (running / configured / spec-only)?', height: 70 },
  { key: 'inv_agent_deployed', name: 'Deployed Agent', hint: 'Last run date? Any open issues in the change log?', height: 60 },
  { key: 'inv_tool_v1', name: 'Custom Tool V1', hint: 'Tool name and what it does. File saved and confirmed working?', height: 60 },
  { key: 'inv_tool_v2', name: 'Custom Tool V2', hint: 'New feature added. Both versions confirmed working?', height: 60 },
];

const COMMITMENT_FIELDS = [
  { key: 'commit_prompts', name: 'Run My Prompts', hint: 'Which 3 prompts? Which day this week?' },
  { key: 'commit_add', name: 'Add One Prompt Per Week', hint: 'First new prompt topic? Due by?' },
  { key: 'commit_agent', name: 'Run My Agent Log Weekly', hint: 'Which day? Calendar blocked?' },
  { key: 'commit_share', name: 'Share One Thing Per Month', hint: 'Who will I show first? When?' },
];

const EVAL_ITEMS = [
  'It runs independently in a browser, no internet connection required.',
  'It does exactly what the combined brief described: original features intact, new feature working.',
  'A colleague could use it with no instructions: the interface explains itself.',
  'The output is production-quality, ready to use at work right now.',
  "The new feature doesn't break anything that was working in V1.",
];

const MSG_BLOCKS = [
  {
    tag: 'MSG 1', key: 'msg1', title: 'Add the Feature',
    desc: 'Describe only the new addition. What does it do? How does the user interact with it? What does it produce? The existing tool is already in context from the code you pasted.',
    msgHeight: 100, responseLabel: 'What came back: does the new feature work?', responseHeight: 60,
  },
  {
    tag: 'MSG 2', key: 'msg2', title: 'Fix or Refine',
    desc: 'One specific thing to change. Did the new feature break anything that was working? Did the implementation miss something from the brief?',
    msgHeight: 80, responseLabel: 'What changed? Does the full tool still pass all five Day 9 criteria?', responseHeight: 60,
  },
  {
    tag: 'MSG 3', key: 'msg3', title: 'Additional Iteration (if needed)',
    desc: 'One change. If you need more, add MSG 3b below. One change per message, always.',
    msgHeight: 70, responseLabel: 'Result:', responseHeight: 54,
  },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

export default function WorkbookDay10({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day10'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day10: { ...(existing.progress?.day10 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day10: { ...(existing.progress?.day10 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day10: { ...(existing.progress?.day10 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 10 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={10} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 10</div>
        <div style={s.headerTitle}>Vibe Coding Part 2 + Program Close</div>
        <div style={s.headerDesc}>You didn't just learn about AI. You built with it.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Part 2 builds on what exists: paste the full Day 9 code as context before your first message so AI adds to the tool instead of replacing it. Describe only the new feature in Message 1. One change per iteration. Get the complete final file at the end and test everything, not just the new addition. The program close is a demo round: every person shows one thing, says one sentence about what it does, and says one number. You didn't just learn about AI. You built with it.
        </div>
      </div>

      <Section title="Part 1 — Vibe Coding Part 2">
        <div style={s.exerciseIntro}>Start from the final HTML file you saved in Day 9. Paste the full code as context first. Then follow the four-message structure.</div>
        <Field label="My Day 10 brief — the feature I'm adding:" value={data.brief} onChange={v => set('brief', v)} minHeight={66} />

        {MSG_BLOCKS.map((msg, i) => (
          <div key={msg.key} style={{ ...s.msgBlock, marginBottom: '14px' }}>
            <div style={s.msgHeader}>
              <div style={s.msgTag}>{msg.tag}</div>
              <div>
                <div style={s.msgTitle}>{msg.title}</div>
                <div style={s.msgDesc}>{msg.desc}</div>
              </div>
            </div>
            <div style={{ ...s.msgField, borderBottom: '1px solid #E8E6E2' }}>
              <div style={s.fieldLabel}>My message:</div>
              <textarea style={{ ...s.textarea, minHeight: msg.msgHeight }} value={data[`${msg.key}_msg`] || ''} onChange={e => set(`${msg.key}_msg`, e.target.value)} />
            </div>
            <div style={{ ...s.msgField, borderBottom: 'none' }}>
              <div style={s.fieldLabel}>{msg.responseLabel}</div>
              <textarea style={{ ...s.textarea, minHeight: msg.responseHeight }} value={data[`${msg.key}_response`] || ''} onChange={e => set(`${msg.key}_response`, e.target.value)} />
            </div>
          </div>
        ))}

        <div style={{ ...s.msgBlock, marginBottom: '0' }}>
          <div style={s.msgHeader}>
            <div style={s.msgTag}>MSG 4</div>
            <div>
              <div style={s.msgTitle}>Get the Final File</div>
              <div style={s.msgDesc}>"Give me the complete updated HTML as a single file with the original tool and the new feature fully incorporated. Ready to save and open in a browser."</div>
            </div>
          </div>
          <div style={{ ...s.msgField, borderBottom: '1px solid #E8E6E2' }}>
            <div style={s.fieldLabel}>My message:</div>
            <textarea style={{ ...s.textarea, minHeight: 60 }} value={data.msg4_msg || ''} onChange={e => set('msg4_msg', e.target.value)} />
          </div>
          <div style={{ ...s.msgField, borderBottom: 'none' }}>
            <div style={s.fieldLabel}>Confirmed: complete tool opens in browser, original features intact, new feature working?</div>
            <div style={{ display: 'flex', gap: '20px', margin: '8px 0' }}>
              {['Yes', 'No'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
                  <input type="radio" name="confirmed_v2" checked={data.confirmed_v2 === opt} onChange={() => set('confirmed_v2', opt)} /> {opt}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Final Tool Evaluation — V2">
        <div style={s.exerciseIntro}>All five criteria apply to the full tool, original features plus new feature. Aim to check every box.</div>
        <div style={s.checklist}>
          {EVAL_ITEMS.map((item, i) => (
            <div key={i} style={{ ...s.checkItem, borderBottom: i < EVAL_ITEMS.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <input type="checkbox" style={{ marginTop: '2px', flexShrink: 0 }} checked={data[`eval_${i}`] || false} onChange={e => set(`eval_${i}`, e.target.checked)} />
              <span style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.6' }}>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Part 2 — Your Program Inventory">
        <div style={s.exerciseIntro}>Name every artifact you built. Where does it live? Is it ready to use? This is your complete output from ten days.</div>
        <div style={s.invTable}>
          {INVENTORY_FIELDS.map((field, i, arr) => (
            <div key={field.key} style={{ ...s.invRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.invLabel}>
                <div style={s.invLabelName}>{field.name}</div>
                <div style={s.invLabelHint}>{field.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: field.height, background: '#FAF8F5' }}
                value={data[field.key] || ''}
                onChange={e => set(field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Part 3 — Demo Prep">
        <div style={s.exerciseIntro}>Every person shows one thing. Prepare your 60-second demo right now so you're not improvising when it's your turn.</div>
        <div style={s.demoBlock}>
          <div style={s.demoHeader}>My 60-Second Demo</div>
          <div style={{ ...s.demoField, borderBottom: '1px solid #E8E6E2' }}>
            <div style={s.fieldLabel}>What I'm showing: (which artifact: tool, agent, workflow redesign, prompt library?)</div>
            <textarea style={{ ...s.textarea, minHeight: 50 }} value={data.demo_what || ''} onChange={e => set('demo_what', e.target.value)} />
          </div>
          <div style={{ ...s.demoField, borderBottom: '1px solid #E8E6E2' }}>
            <div style={s.fieldLabel}>My one sentence: "This is [name of thing] and it [what it does for who]:"</div>
            <textarea style={{ ...s.textarea, minHeight: 54 }} value={data.demo_sentence || ''} onChange={e => set('demo_sentence', e.target.value)} />
          </div>
          <div style={{ ...s.demoField, borderBottom: 'none' }}>
            <div style={s.fieldLabel}>My one number: (time saved / frequency / before-and-after, one concrete number)</div>
            <textarea style={{ ...s.textarea, minHeight: 50 }} value={data.demo_number || ''} onChange={e => set('demo_number', e.target.value)} />
          </div>
        </div>
      </Section>

      <Section title="What Comes Next — Your Four Commitments">
        <div style={s.exerciseIntro}>Write down the specific action for each commitment. Vague intentions don't survive the first week without a program.</div>
        <div style={s.commitTable}>
          {COMMITMENT_FIELDS.map((field, i, arr) => (
            <div key={field.key} style={{ ...s.commitRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.commitLabel}>
                <div style={s.commitLabelName}>{field.name}</div>
                <div style={s.commitLabelHint}>{field.hint}</div>
              </div>
              <textarea
                style={{ ...s.textarea, flex: 1, border: 'none', minHeight: 60, background: '#FAF8F5' }}
                value={data[field.key] || ''}
                onChange={e => set(field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Final Reflection — Program Close">
        <Field
          label="1. On Day 1 you took the AI Readiness Snapshot. Look at your score from then. Look at what you built across ten days. What's the most significant change, not just in what you can do, but in how you think about your work?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={90}
        />
        <Field
          label="2. What's the one thing you built in this program that you'll still be using six months from now? Why that one?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 10">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <div style={s.closeBox}>
        <div style={s.closeTitle}>From Curious to Capable</div>
        <div style={s.closeSub}>You didn't just learn about AI.</div>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#D4A847', marginBottom: '16px' }}>You built with it.</div>
        {[
          'A Prompt Library that runs on real work',
          'A redesigned workflow with a number that proves the value',
          "A deployed agent that works while you're not watching",
          "A custom tool that didn't exist ten days ago",
        ].map((item, i) => (
          <div key={i} style={s.closeItem}>
            <span style={{ color: '#D4A847', fontWeight: '700' }}>✓</span> {item}
          </div>
        ))}
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff', marginTop: '20px' }}>Congratulations.</div>
        <div style={s.closeFooter}>The Corporate Fixers · www.thecorporatefixers.com</div>
      </div>

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 10 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 10 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 10 as Complete?</div>
            <div style={s.modalText}>This completes the From Curious to Capable program. Congratulations on finishing all ten days.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '18px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  msgBlock: { border: '1.5px solid #E8E6E2' },
  msgHeader: { background: '#1B2A4A', padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: '12px' },
  msgTag: { background: '#D4A847', color: '#1B2A4A', fontSize: '10px', fontWeight: '700', padding: '3px 10px', letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0, marginTop: '1px' },
  msgTitle: { fontSize: '12px', fontWeight: '700', color: '#fff' },
  msgDesc: { fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '2px', lineHeight: '1.4' },
  msgField: { padding: '10px 14px' },
  checklist: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  checkItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px' },
  invTable: { border: '1.5px solid #E8E6E2' },
  invRow: { display: 'flex' },
  invLabel: { width: '150px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  invLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  invLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.4' },
  demoBlock: { border: '1.5px solid #E8E6E2' },
  demoHeader: { background: '#1B2A4A', color: '#D4A847', padding: '10px 14px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  demoField: { padding: '10px 14px' },
  commitTable: { border: '1.5px solid #E8E6E2' },
  commitRow: { display: 'flex' },
  commitLabel: { width: '150px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  commitLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  commitLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.4' },
  closeBox: { background: '#1B2A4A', padding: '28px', marginBottom: '14px', textAlign: 'center' },
  closeTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#D4A847', marginBottom: '8px' },
  closeSub: { fontSize: '14px', color: '#fff', marginBottom: '4px', fontWeight: '300' },
  closeItem: { fontSize: '13px', color: 'rgba(255,255,255,0.9)', lineHeight: '2', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  closeFooter: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '16px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
